import { useEffect, useState } from "react";
import { Headphones, ChevronRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { MaterialAudioControls } from "@/components/MaterialAudioControls";

/**
 * Dashboard-kort: "Fortsätt lyssna" — visar material där eleven är mitt
 * i en TTS-uppspelning. Klick på play startar uppspelning direkt från
 * sparad position (via MaterialAudioControls som hämtar progress vid mount).
 *
 * Visas bara om det finns minst 1 pågående lyssning. Tyst om inget finns
 * så Dashboarden inte blir kluttrad för nya användare.
 */
export function ContinueListeningCard({ onOpenEvent }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/materials/listen/in-progress");
        if (cancelled) return;
        setItems(r.data?.items || []);
      } catch (_e) {
        /* ok */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || items.length === 0) return null;

  const handleRemove = async (materialId, e) => {
    e?.stopPropagation();
    // Optimistisk uppdatering — ta bort direkt från UI
    setItems((prev) => prev.filter((it) => it.material_id !== materialId));
    try {
      await api.delete(`/materials/${materialId}/speak/progress`);
    } catch (_err) {
      // Vid fel — hämta om listan så vi inte visar fel state
      try {
        const r = await api.get("/materials/listen/in-progress");
        setItems(r.data?.items || []);
      } catch (_e) {
        /* ok */
      }
    }
  };

  return (
    <div className="card-soft p-4 mb-4 fade-up" data-testid="continue-listening-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[color:var(--jarvis)] text-white flex items-center justify-center">
          <Headphones size={15} />
        </div>
        <div>
          <p className="text-sm font-semibold">Fortsätt lyssna</p>
          <p className="text-[10px] text-[color:var(--text-tertiary)]">
            Där du slutade · {items.length} {items.length === 1 ? "material" : "material"}
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {items.slice(0, 3).map((it) => {
          const pct = it.total_chunks > 1
            ? Math.round((it.chunk_index / it.total_chunks) * 100)
            : 0;
          return (
            <li
              key={it.material_id}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-[color:var(--bg-elevated)] transition"
              data-testid={`continue-listening-item-${it.material_id}`}
            >
              <MaterialAudioControls
                materialId={it.material_id}
                filename={it.filename}
                compact
              />
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  className="text-left w-full"
                  onClick={() => it.event_id && onOpenEvent?.(it.event_id)}
                  title={it.event_summary ? `Öppna ${it.event_summary}` : ""}
                >
                  <p className="text-xs font-medium truncate">{it.filename}</p>
                  {it.event_summary && (
                    <p className="text-[10px] text-[color:var(--text-secondary)] truncate">
                      {it.event_category === "test" ? "Prov" : "Läxa"}
                      {it.event_owner ? ` · ${it.event_owner}` : ""}
                      {" · "}
                      {it.event_summary}
                    </p>
                  )}
                  {it.total_chunks > 1 && (
                    <div className="mt-1.5 h-1 bg-[color:var(--bg-elevated)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[color:var(--jarvis)] rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                        data-testid={`continue-listening-progress-${it.material_id}`}
                      />
                    </div>
                  )}
                </button>
              </div>
              {it.event_id && (
                <ChevronRight
                  size={14}
                  className="text-[color:var(--text-tertiary)] flex-shrink-0"
                />
              )}
              <button
                type="button"
                onClick={(e) => handleRemove(it.material_id, e)}
                className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-[color:var(--danger-soft)] text-[color:var(--text-tertiary)] hover:text-[color:var(--danger)] flex items-center justify-center transition"
                title="Ta bort från Fortsätt lyssna"
                aria-label="Ta bort från Fortsätt lyssna"
                data-testid={`continue-listening-remove-${it.material_id}`}
              >
                <X size={13} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
